import { useEffect, useRef, useState } from "react";
import {
  Plus, Pencil, ShieldCheck, Trash2, Loader2, FileText, Search, ChevronDown,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
} from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import DocumentTransactionsService from "./DocumentTransactions.service";
import TemplateEditor from "./TemplateEditor";
import AccessControlPanel from "./AccessControlPanel";
import type { DocumentTemplate, TemplateCursor } from "./Documents.types";

const PAGE_SIZE = 10;

const pulse = "animate-pulse rounded bg-muted";

function SkeletonRows({ count }: { count: number }) {
  return (
    <>
      {Array.from({ length: count }).map((_, i) => (
        <TableRow key={i} className="border-border/40">
          <TableCell className="pl-6 py-4"><div className={`h-3.5 w-40 ${pulse}`} /></TableCell>
          <TableCell><div className={`h-5 w-16 ${pulse}`} /></TableCell>
          <TableCell><div className={`h-5 w-16 ${pulse}`} /></TableCell>
          <TableCell className="pr-6">
            <div className="flex justify-end gap-1">
              <div className={`h-8 w-8 ${pulse}`} />
              <div className={`h-8 w-8 ${pulse}`} />
              <div className={`h-8 w-8 ${pulse}`} />
            </div>
          </TableCell>
        </TableRow>
      ))}
    </>
  );
}

export default function DocumentsPage() {
  const service = useRef(new DocumentTransactionsService());

  const [ templates, setTemplates ] = useState<DocumentTemplate[]>([]);
  const [ loading, setLoading ] = useState(false);
  const [ loadingMore, setLoadingMore ] = useState(false);
  const [ search, setSearch ] = useState("");
  const [ hasMore, setHasMore ] = useState(false);
  const nextCursorRef = useRef<TemplateCursor | null>(null);

  const [ editorOpen, setEditorOpen ] = useState(false);
  const [ editingTemplate, setEditingTemplate ] = useState<DocumentTemplate | null>(null);

  const [ accessOpen, setAccessOpen ] = useState(false);
  const [ accessTemplate, setAccessTemplate ] = useState<DocumentTemplate | null>(null);

  const [ deleteTarget, setDeleteTarget ] = useState<DocumentTemplate | null>(null);
  const [ deleteLoading, setDeleteLoading ] = useState(false);

  // RDB has no OFFSET — every fetch is either "start over" or "the batch
  // after the last cursor," so a "Load More" list replaces numbered pages.
  const fetchFromStart = async () => {
    setLoading(true);
    try {
      const result = await service.current.fetchTemplates({ search, limit: PAGE_SIZE, cursor: null });
      if (result.success) {
        setTemplates(result.templates);
        setHasMore(result.hasMore);
        nextCursorRef.current = result.nextCursor;
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchFromStart(); }, [ search ]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadMore = async () => {
    if (!nextCursorRef.current) return;
    setLoadingMore(true);
    try {
      const result = await service.current.fetchTemplates({ search, limit: PAGE_SIZE, cursor: nextCursorRef.current });
      if (result.success) {
        setTemplates((prev) => [ ...prev, ...result.templates ]);
        setHasMore(result.hasMore);
        nextCursorRef.current = result.nextCursor;
      }
    } finally {
      setLoadingMore(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    try {
      const result = await service.current.deleteTemplate(deleteTarget.id);
      if (result.success) {
        setDeleteTarget(null);
        await fetchFromStart();
      }
    } finally {
      setDeleteLoading(false);
    }
  };

  const accessSummary = (t: DocumentTemplate) => {
    const parts = [
      t.access.student.mode === "open" ? "Students: Open" : `Students: ${t.access.student.allowedIdsCount} granted`,
      t.access.faculty.mode === "open" ? "Faculty: Open" : `Faculty: ${t.access.faculty.allowedIdsCount} granted`,
    ];
    return parts.join(" · ");
  };

  return (
    <div className="min-h-screen bg-[oklch(0.97_0.01_90)] p-6 md:p-8 text-foreground">

      <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Document Templates</h1>
          <p className="mt-1 text-sm text-muted-foreground">Design certificates and letters students & faculty can self-serve download</p>
        </div>
        <Button onClick={() => { setEditingTemplate(null); setEditorOpen(true); }} className="gap-2 shrink-0">
          <Plus className="h-4 w-4" /> New Template
        </Button>
      </div>

      <Card className="border border-border/60 shadow-sm overflow-hidden bg-card">
        <CardHeader className="flex flex-col gap-4 pb-4 sm:flex-row sm:items-center sm:justify-between border-b border-border/40 bg-muted/5">
          <div>
            <CardTitle className="text-base font-semibold">Templates</CardTitle>
            <CardDescription className="text-sm">Every saved certificate/document design for this institute</CardDescription>
          </div>
          <div className="relative w-full sm:w-64">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input placeholder="Search templates..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 text-sm border-border/60" />
          </div>
        </CardHeader>

        <CardContent className="p-0">
          <Table>
            <TableHeader className="bg-muted/30">
              <TableRow className="border-border/60 hover:bg-transparent">
                <TableHead className="pl-6 font-bold text-[11px] uppercase tracking-widest">Name</TableHead>
                <TableHead className="font-bold text-[11px] uppercase tracking-widest">Status</TableHead>
                <TableHead className="font-bold text-[11px] uppercase tracking-widest">Access</TableHead>
                <TableHead className="pr-6 text-right font-bold text-[11px] uppercase tracking-widest">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <SkeletonRows count={PAGE_SIZE} />
              ) : templates.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} className="text-center py-16 text-sm text-muted-foreground">No templates yet — create one to get started</TableCell>
                </TableRow>
              ) : (
                templates.map((t) => (
                  <TableRow key={t.id} className="border-border/40 hover:bg-muted/40 transition-colors group">
                    <TableCell className="pl-6 py-4">
                      <div className="flex items-center gap-2">
                        <div className="h-8 w-8 rounded bg-primary/5 flex items-center justify-center border border-primary/10">
                          <FileText className="h-4 w-4 text-primary" />
                        </div>
                        <div>
                          <p className="font-semibold text-sm">{t.name}</p>
                          {t.description && <p className="text-[11px] text-muted-foreground">{t.description}</p>}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={t.isActive ? "bg-emerald-500/10 text-emerald-600 border-0" : "bg-muted text-muted-foreground border-0"}>
                        {t.isActive ? "Active" : "Inactive"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">{accessSummary(t)}</TableCell>
                    <TableCell className="pr-6 text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="icon" className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity" onClick={() => { setAccessTemplate(t); setAccessOpen(true); }}>
                          <ShieldCheck className="h-4 w-4 text-muted-foreground" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { setEditingTemplate(t); setEditorOpen(true); }}>
                          <Pencil className="h-4 w-4 text-muted-foreground" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-red-600 hover:text-red-700" onClick={() => setDeleteTarget(t)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>

          {/* LOAD MORE FOOTER — cursor pagination has no total count / page jump */}
          {!loading && (templates.length > 0 || hasMore) && (
            <div className="flex flex-col items-center gap-2 px-6 py-4 border-t border-border/60 bg-muted/5">
              <p className="text-[11px] text-muted-foreground">
                Showing <span className="font-bold text-foreground">{templates.length}</span> template{templates.length !== 1 ? "s" : ""}
              </p>
              {hasMore && (
                <Button variant="outline" size="sm" className="h-8 border-border/60 text-[11px] font-bold gap-1.5" disabled={loadingMore} onClick={loadMore}>
                  {loadingMore ? <Loader2 className="h-3 w-3 animate-spin" /> : <ChevronDown className="h-3 w-3" />}
                  Load More
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <TemplateEditor
        open={editorOpen}
        onOpenChange={setEditorOpen}
        template={editingTemplate}
        onSaved={fetchFromStart}
      />

      <AccessControlPanel
        open={accessOpen}
        onOpenChange={setAccessOpen}
        template={accessTemplate}
        onChanged={fetchFromStart}
      />

      <Dialog open={!!deleteTarget} onOpenChange={(v) => { if (!deleteLoading && !v) setDeleteTarget(null); }}>
        <DialogContent className="max-w-md border-border/60">
          <DialogHeader className="pb-2">
            <DialogTitle className="text-lg font-bold">Delete template?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground pb-2">
            This will permanently remove <span className="font-semibold text-foreground">{deleteTarget?.name}</span>. This action cannot be undone.
          </p>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="ghost" className="text-xs font-bold" disabled={deleteLoading} onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button variant="destructive" className="text-xs font-bold min-w-[120px]" disabled={deleteLoading} onClick={confirmDelete}>
              {deleteLoading ? <><Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" />Deleting...</> : <><Trash2 className="h-3.5 w-3.5 mr-1.5" />Delete</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
